import crypto from 'crypto-es'
import partition from 'lodash/partition'
import startCase from 'lodash/startCase'
import uniq from 'lodash/uniq'
import { DateTime } from 'luxon'
import moment, { Moment } from 'moment'
// @ts-ignore
import uuidv4 from 'uuid/v4'

import client from 'api/client'
import { REACT_APP_ALMANAC_IMAGE_SERVICE_URL } from 'env'
import { schema } from 'prose-core'
import { convertToMB } from 'proseShared/nodeViews/Download/Utils'
import { Step, Transform } from 'prosemirror-transform'
import * as Actions from 'redux/actions'
import store from 'redux/store'
import { ArticleFlat, ArticleMeta, ArticleNoElements, Auth, AuthWithHeaders, Workspace } from 'types'
import { CLOUDINARY_HOSTNAME, GOOGLEUSERCONTENT_HOSTNAME } from 'utils/constants'
import { reportError } from 'utils/error'
import {
  ALMANAC_CORE_WORKSPACE_ID,
  DEFAULT_DOC_TITLE,
  NERUDA_SCHEMA_VERSION,
  PREVIEW_IMAGE_HEIGHT,
  PREVIEW_IMAGE_WIDTH,
} from './constants'

// tslint:disable-next-line:no-empty
export const NOOP = () => {}

export const getParamsForFileUpload = async (file?: File, body = {}) => {
  if (!file) return

  const res = await client.getUploadUrl({ ...body, filename: file.name, filesize: convertToMB(file.size) })
  if (res.type !== 'success') return null
  const { url, error } = res.data

  if (error) return { error }

  return { url, body: file, method: 'PUT', meta: { url: url.split('?')[0] } }
}

export const authFromResponse = (
  data: Omit<Auth, 'impersonatedBy'>,
  headers: { [key: string]: string },
): AuthWithHeaders => {
  return { ...data, headers: { authorization: headers.authorization } }
}

/**
 * Why this method instead of the nice, new async clipboard API? Because that one requires the user's permission.
 *
 * https://davidea.st/articles/copy-and-paste-clipboard-api
 */
export const copyToClipboard = (str: string) => {
  const el = document.createElement('textarea')
  el.value = str
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

export const isEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

export const onlyNumbers = (value: string) => {
  return parseInt(value.replace(/\D/g, ''), 10)
}

/**
 * Similar to scrollIntoView() but for scrolling within a container element
 *
 * @param {HTMLElement} el the element we are scrolling into view
 * @param {HTMLElement} containerEl the container element we are scrolling on
 * @param {HTMLElement} firstContainerItemEl the first container item element needed
 *                      as a ref to figure out relative scroll difference to apply
 */
export const scrollContainerItemIntoView = (
  el: HTMLElement,
  containerEl: HTMLElement,
  firstContainerItemEl: HTMLElement,
) => {
  if (!el) return
  containerEl.scrollTo({
    top: el.getBoundingClientRect().top - firstContainerItemEl.getBoundingClientRect().top,
    behavior: 'smooth',
  })
}

export const truncate = (s: string, l: number, ellipsis = '…') => {
  if (s.length > l) {
    return `${s.substring(0, l).trim()}${ellipsis}`
  }
  return s
}

export const pluralize = (s: string, n: number) => {
  if (n === 1) return s
  if (s === 'day') return 'days'
  if (s.endsWith('s')) return `${s}es`
  if (s.endsWith('y')) return `${s.slice(0, -1)}ies`
  return `${s}s`
}

export const makePossessiveSingular = (s: string): string => {
  if (!s) return ''

  if (s.endsWith('s') || s.endsWith('S')) return `${s}'`

  return `${s}'s`
}

export const possessivePluralize = (s: string) => {
  return `${s}'s`
}

export const pluralizeWithNumber = (n: number, s: string) => `${n} ${pluralize(s, n)}`

export const pluralizeWithA = (s: string, n: number) => {
  return `${n === 1 ? 'a' : n} ${pluralize(s, n)}`
}

export const fullName = ({
  first_name: first,
  last_name: last,
}: {
  first_name: string | null
  last_name: string | null
}) => {
  return `${first || ''} ${last || ''}`
}

export const formatMomentDateInDays = (date: Moment) => {
  if (moment().isSame(date, 'day')) {
    return 'Today'
  }

  if (moment().diff(date, 'days') > 0) {
    if (moment().diff(date, 'days') === 1) {
      return 'Yesterday'
    } else {
      return moment().diff(date, 'days') + ' days ago'
    }
  }

  let today = moment()
  let future = moment(date)

  if (future.diff(today, 'days') >= 0) {
    if (future.diff(today, 'days') === 0) {
      return 'Tomorrow'
    } else if (future.diff(today, 'days') + 1 <= 7) {
      return 'Next ' + moment(date).format('dddd')
    } else {
      return moment(date).format('MMM DD')
    }
  }
}

export const ourUuid = (): string => {
  return uuidv4()
}

export function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function getArticleLink(
  { article_link }: { article_link: string },
  options: { edit?: boolean } = { edit: false },
): string {
  if (!options.edit) return article_link
  return `${article_link}/edit`
}

export function titleCase(s: string | undefined): string {
  if (!s || s.length === 0) return ''
  return `${s[0].toUpperCase()}${s.slice(1)}`
}

// @note if using this, you will most likely want to check article.made_public too
//   for example:
//     ```if (isAlmanacArticle(article) || article.made_public) { ...stuff }```
//
// @note This will be deprecated once below ticket is complete
// @see https://www.pivotaltracker.com/n/projects/2414180/stories/172054125
export function isAlmanacArticle(article: ArticleMeta): boolean {
  return article.company.id === ALMANAC_CORE_WORKSPACE_ID
}

/**
 * Returns a bool on whether a given article is in one of the user's workspaces
 *
 * @param {ArticleNoElements} article
 * @param {Workspace[]} workspaces
 * @returns {boolean} True if doc is in one of user's workspaces, otherwise false
 */
export const isDocInWorkspaces = (article: ArticleNoElements, workspaces: Workspace[]): boolean =>
  workspaces.some((w) => w.id === article.company.id)

/**
 * Get initials from a string based on spaces
 *
 * @param {?string} name Input
 * @param {?number} length Input
 * @returns {string} Initials
 */
export const getInitials = (name?: string | null): string => {
  if (!name) return ''
  let initialsArray = name
    .split(' ')
    .map((s) => s.charAt(0).toUpperCase())
    .filter((s) => /^\w/.test(s)) // to ignore emojis

  if (!initialsArray.length) return name?.slice(0, 1)

  const firstInitial = initialsArray[0]
  const lastInitial = initialsArray.length > 1 ? initialsArray[initialsArray.length - 1] : ''

  return `${firstInitial}${lastInitial}`
}

export const getAbbreviatedDisplayName = (name?: string | null): string => {
  if (!name) return ''

  if (name === 'Anonymous User') return name

  const names = name.split(' ')
  return names
    .map((s, index) => (index === names.length - 1 ? s.charAt(0).toUpperCase() : s))
    .filter((s) => /^\w/.test(s)) // to ignore emojis
    .join(' ')
    .concat('.')
}

/**
 * Returns a SHA-1 hexadecimal signature
 *
 * @param {string} s UTF-8 encoded string
 * @returns {string} SHA-1 hexadecimal signature
 */
export const sha1Hex = (s: string): string => crypto.SHA1(s).toString(crypto.enc.Hex)

/**
 * Get creator id to show in read/edit view
 */
export const getCreatorId = (article: ArticleFlat) => {
  return article.creator_id
}

/**
 * Returns text content from html string
 *
 * @param {string} htmlString Html string
 * @returns {string} Text content
 */
export const getTextContentFromHtmlString = (htmlString: string): string => {
  const title = document.createElement('span')
  title.innerHTML = htmlString || ''
  return title.textContent || ''
}

/**
 * Returns formatted hex color code
 *
 * @param {?(string | null)} hex Hex color code
 * @param {?string} [fallbackHex] Optional fallback hex color code
 * @returns {string} Formatted hex color code
 */
export const getFormattedHex = (hex?: string | null, fallbackHex?: string): string => {
  let formattedHex = fallbackHex || ''

  if (!hex) return formattedHex

  if (hex.length === 6) hex = `#${hex}`
  if (typeof hex === 'string' && hex.length === 7 && !isNaN(Number('0x' + hex.slice(1, 6)))) {
    formattedHex = hex
  }

  return formattedHex
}

/**
 * Returns unix timestamp of Luxon-based date time object
 *
 * @param {DateTime} dateTime Luxon-based date time object
 * @returns {number} unix timestamp
 */
export const getUnixEpoch = (dateTime: DateTime): number => Math.round(dateTime.toMillis() / 1000)

/**
 * Gets the workspace object with workspace Id
 *
 * @param {number} workspaceId ID of workspace
 * @returns {(Workspace|undefined)} A workspace object, or undefined if no workspaces set
 */
export const getWorkspace = (workspaceId: number): Workspace | undefined => {
  const { workspaces } = store.getState().workspace

  let workspace
  if (!!workspaces && workspaces.length) {
    workspace = workspaces.find((w) => w.id === workspaceId)
  }

  return workspace
}

/**
 * Gets the current workspace object based on the currentWorkspaceId stored in redux
 *
 * @returns {(Workspace|undefined)} Current workspace object, or undefined if no workspaces set
 */
export const getCurrentWorkspace = (): Workspace | undefined => {
  const {
    workspace: { currentWorkspaceId },
  } = store.getState()

  return getWorkspace(currentWorkspaceId)
}

/**
 * Sets data to session storage
 *
 * @param {string} key Session storage key
 * @param {Object} data Data object to be stored in session storage
 * @param {boolean} [overwrite=false] True to overwrite existing data, if key exists. Otherwise defaults to safely append new data values only.
 */
export const setSessionStorage = (key: string, data: { [key: string]: any }, overwrite: boolean = false) => {
  try {
    const item = sessionStorage.getItem(key)
    const value = JSON.stringify(!!item && !overwrite ? { ...JSON.parse(item), ...data } : data)
    sessionStorage.setItem(key, value)
  } catch (e) {
    reportError(new Error('setLocalSession error'), { severity: 'info', metaData: e as Error })
  }
}

/**
 * Resolves current workspace with user's workspace, if user logged in.
 * Fixes an issue with user manually entering in a private url, ie. '/settings/workspace' from '/home'
 * and not displaying the correct info in workspace dropdown and nav rail.
 */
export const resolveCurrentWorkspace = () => {
  const {
    auth,
    workspace: { currentWorkspaceId },
  } = store.getState()

  if (!!auth && currentWorkspaceId === ALMANAC_CORE_WORKSPACE_ID) {
    store.dispatch(Actions.currentWorkspaceIdSet(auth.company_id))
  }
}

export const getPersonalWorkspaceKey = (): string | undefined => {
  const auth = store.getState().auth

  if (!auth) {
    return
  }

  return auth.company_key
}

export const createNewArticle = ({
  workspaceKey,
  projectId,
  channelId,
  title,
  header_emoji,
  externalLink,
  steps,
  homepageDoc,
  externalFileIcon,
  createArticleViewEvent,
  skipHeading,
}: {
  workspaceKey: string
  projectId?: number | null
  channelId?: number
  title?: string
  header_emoji?: string | null
  externalLink?: string
  steps?: Step[]
  homepageDoc?: boolean
  createArticleViewEvent?: boolean
  externalFileIcon?: string
  skipHeading?: boolean
}) => {
  let titleSteps: Step[] = []

  if (title && title !== DEFAULT_DOC_TITLE && !steps?.length) {
    // Create an empty document
    let doc = schema.node('doc', {}, [schema.node('paragraph', {}, [])])

    if (!skipHeading) {
      // Generate nodes for title and description
      let node = [schema.node('heading', { level: 1 }, [schema.text(title)]), schema.node('paragraph', {}, [])]

      // Add nodes to the empty document
      const transform = new Transform(doc)
      transform.replaceWith(0, 1, node)
      titleSteps = transform.steps
    }
  }

  return client.createArticle(
    {
      project: projectId,
      title: title || DEFAULT_DOC_TITLE,
      header_emoji,
      external_link: externalLink,
      schema_version: NERUDA_SCHEMA_VERSION,
      external_file_icon: externalFileIcon,
    },
    workspaceKey,
    homepageDoc,
    channelId,
    titleSteps.length ? titleSteps : steps,
    createArticleViewEvent,
  )
}

export const getUserProfilePreviewImageUrl = (userId: number): string => {
  const previewImageUrl = new URL(REACT_APP_ALMANAC_IMAGE_SERVICE_URL)
  previewImageUrl.pathname = `/images/v1/users/${userId}/preview_image.html`

  const searchParams = new URLSearchParams()
  searchParams.set('height', PREVIEW_IMAGE_HEIGHT.toString())
  searchParams.set('width', PREVIEW_IMAGE_WIDTH.toString())

  previewImageUrl.search = searchParams.toString()

  return previewImageUrl.toString()
}

export const areAllEqual = (...args: any[]) => {
  const len = args.length
  for (let i = 1; i < len; i++) {
    if (args[i] === null || args[i] !== args[i - 1]) return false
  }
  return true
}

export const optimizedImageUrlString = (urlString: string, size?: string) => {
  try {
    const url = new URL(urlString)

    if (url.hostname === CLOUDINARY_HOSTNAME) {
      const pathComponents = url.pathname.split('/')

      let newPathComponents = pathComponents.slice(1, 4)
      newPathComponents.push('q_auto,f_auto')

      if (size) {
        newPathComponents.push(`c_scale,w_${size}`)
      }

      newPathComponents = newPathComponents.concat(pathComponents.slice(4))

      url.pathname = newPathComponents.join('/')
    } else if (url.hostname.includes(GOOGLEUSERCONTENT_HOSTNAME)) {
      if (size) {
        // update size param to match desired size
        const sizeMatch = /s([0-9]*)/
        const splitPathname = url.pathname.split('=')
        let params = splitPathname[1] || ''
        params = params.replace(sizeMatch, `s${size}`)
        const prefix = params.length > 1 ? '-' : ''
        params += `${prefix}w${size}`
        url.pathname = `${splitPathname[0]}=${params}`
      }
    }

    return url.toString()
  } catch {
    return urlString
  }
}

export const buildErrorMessage = (
  errors?: { detail: string; source: { pointer: string } }[],
  replaceAttributeName: { [attributeName: string]: string | null } = {},
) =>
  errors &&
  errors
    .map(({ detail, source: { pointer } }) => {
      const attributeComponents = pointer.split('/')
      const attribute = attributeComponents[attributeComponents.length - 1]

      if (attribute in replaceAttributeName) {
        const replacement = replaceAttributeName[attribute]

        return replacement === null ? detail : `${replacement} ${detail}`
      }

      return `${startCase(attribute)} ${detail}`
    })
    .join(', ')

export const isMacintosh = () => {
  return navigator.platform.indexOf('Mac') > -1
}

export const stopPropagation = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
  e.stopPropagation()
}

/**
 * Loads an image with a progress callback.
 *
 * The `onprogress` callback will be called by XMLHttpRequest's onprogress
 * event, and will receive the loading progress ratio as a whole number.
 * However, if it's not possible to compute the progress ratio, `onprogress`
 * will be called only once passing -1 as progress value. This is useful to,
 * for example, change the progress animation to an undefined animation.
 *
 * Shamelessly borrowed from stackoverflow: https://stackoverflow.com/questions/14218607/javascript-loading-progress-of-an-image
 */
export const loadImage = (imageUrl: string, onprogress: (progress: number) => void) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let notifiedNotComputable = false

    xhr.open('GET', imageUrl, true)
    xhr.responseType = 'arraybuffer'

    xhr.onprogress = function (ev) {
      if (ev.lengthComputable) {
        onprogress(Math.floor((ev.loaded / ev.total) * 100))
      } else {
        if (!notifiedNotComputable) {
          notifiedNotComputable = true
          onprogress(-1)
        }
      }
    }

    xhr.onloadend = function () {
      if (!xhr.status.toString().match(/^2/)) {
        reject(xhr)
      } else {
        if (!notifiedNotComputable) {
          onprogress(100)
        }

        const options: { type?: string } = {}
        const headers = xhr.getAllResponseHeaders()
        const m = headers.match(/^Content-Type:\s*(.*?)$/im)

        if (m && m[1]) {
          options.type = m[1]
        }

        const blob = new Blob([this.response], options)

        resolve(URL.createObjectURL(blob))
      }
    }

    xhr.send()
  })
}

export const kFormatter = (num?: number | null, opts?: { decimals?: number; whenUnset?: string }) => {
  if (!num) return opts?.whenUnset || ''

  const round = num % 1000 === 0
  const kNum = num / 1000
  return num > 999 ? (round ? kNum : kNum.toFixed(opts?.decimals || 1)) + 'k' : num.toString()
}

export const parseEmails = (fromString: string) => {
  const uniqEmails = uniq(fromString.split(/[\s,;]/).filter((s) => s.length))
  return partition(uniqEmails, isEmail)
}

// This is hard to mock in jest
export function isDocumentHidden() {
  return document.hidden
}

export function ignoreFirstCall(fn: () => void): () => void {
  let ignore = true
  return () => {
    if (ignore) {
      ignore = false
      return
    }
    fn()
  }
}

/**
 * Turns a number or valid numeric string into a pixel value.
 *
 * Anything else is returned as null.
 * */
export function safePixel(val: number | string | undefined | null): string | null {
  if (val != null) {
    if (isNaN(val as unknown as number)) {
      return null
    }
    return `${val}px`
  }
  return val ?? null
}
